/**
 * rebookNoticeCopy — the words for each rebook notice (BON-146), and its title.
 *
 * A `.tsx` module rather than a string table because two of these bodies carry
 * markup that is the point of them: the already-rebooked refusal turns "open the
 * booking it moved to" from an instruction into a LINK, using the successor id
 * the server put on the 409, and the 500 shows the request reference in mono so
 * it can be read down a phone to engineering.
 *
 * Separated from `RebookNoticeCard` so the card is a frame and this is copy —
 * one switch over seven cases in a component that also does layout was over the
 * complexity cap, and rightly: they change for different reasons.
 */
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { statusDisplay } from './bookingDetailModel';
import type { RebookNoticeDetail } from './rebookNotices';

export const NOTICE_TITLE: Record<RebookNoticeDetail['kind'], string> = {
  cutoffBlocked: 'Past the rebooking cutoff',
  cutoffOverride: 'Past the cutoff — override available',
  alreadyRebooked: 'Already moved',
  notConfirmed: 'This booking can’t be moved',
  noCapacity: 'No room left',
  noPick: 'No workshop selected',
  failure: 'Couldn’t move this booking',
};

function alreadyRebookedBody(
  successorBookingId: string | undefined,
  code: string | null | undefined,
): React.ReactNode {
  if (!successorBookingId) {
    return (
      <>
        This booking has already been moved, so it no longer holds seats. Reload the page to see
        where it went.
      </>
    );
  }
  return (
    <>
      This booking has already been moved, so it no longer holds seats. Rebook the one it moved to
      instead.
      <Link
        to={`/admin/bookings/${successorBookingId}`}
        className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-moss-700 underline dark:text-moss-300"
      >
        Open {code ?? 'the new booking'}
        <ChevronRight size={14} />
      </Link>
    </>
  );
}

function noCapacityBody(available?: number, requested?: number): React.ReactNode {
  const counts =
    available !== undefined && requested !== undefined
      ? ` — ${available} ${available === 1 ? 'seat' : 'seats'} left, and this booking needs ${requested}`
      : '';
  return <>That workshop filled up while you were choosing{counts}. Pick another one.</>;
}

function failureBody(message: string, requestId?: string): React.ReactNode {
  if (!requestId) return <>{message}</>;
  return (
    <>
      {message} Nothing was changed and the customer wasn&rsquo;t emailed. If it keeps failing, send
      engineering this reference: <span className="font-mono font-semibold">{requestId}</span>.
    </>
  );
}

export function noticeBody(d: RebookNoticeDetail): React.ReactNode {
  switch (d.kind) {
    case 'cutoffBlocked':
      return (
        <>
          Bookings can&rsquo;t be moved after 10:00 AM on the day of the workshop, and this one
          starts {d.workshopWhen}. Ask someone who can override the cutoff, or refund instead.
        </>
      );
    case 'cutoffOverride':
      return (
        <>
          Bookings can&rsquo;t normally be moved after 10:00 AM on the day of the workshop — this
          one starts {d.workshopWhen}. Your permissions let you move it anyway.
        </>
      );
    case 'alreadyRebooked':
      return alreadyRebookedBody(d.successorBookingId, d.successorConfirmationCode);
    case 'notConfirmed':
      return (
        <>
          This booking is {d.status ? statusDisplay(d.status).label.toLowerCase() : 'not confirmed'}
          , so there is nothing to move. Create a new booking for this customer instead.
        </>
      );
    case 'noCapacity':
      return noCapacityBody(d.available, d.requested);
    case 'noPick':
      return <>Pick the workshop to move this booking to, then move it.</>;
    default:
      return failureBody(d.message, d.requestId);
  }
}
